import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaBell, FaCheckDouble, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { getCarrierFailureTexts } from '../utils/carrierFailureDiagnosis';
import './NotificationBell.css';

const POLL_INTERVAL_MS = 60000;
// The bell (badge, « Alerts » tab) shows critical problems only; information
// notices — drafts the AI left for a ticket's assignee, tickets reopened by a
// customer reply — have their own tab (backend ?level=info).
const LEVEL_CRITICAL = 'critical';
const LEVEL_INFO = 'info';

const NotificationBell = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [infoNotifications, setInfoNotifications] = useState([]);
  const [infoUnreadCount, setInfoUnreadCount] = useState(0);
  const [level, setLevel] = useState(LEVEL_CRITICAL);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const pollRef = useRef(null);

  const fetchLevel = useCallback(async (wanted) => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    try {
      const response = await axios.get('/api/notifications', {
        headers: { Authorization: `Bearer ${token}` },
        params: { level: wanted }
      });
      const items = response.data?.items || [];
      const unread = response.data?.unread_count || 0;
      if (wanted === LEVEL_INFO) {
        setInfoNotifications(items);
        setInfoUnreadCount(unread);
      } else {
        setNotifications(items);
        setUnreadCount(unread);
      }
    } catch (error) {
      // Silent: the bell must never break the shell. Next poll retries.
    }
  }, []);

  // The poll keeps the badge: critical notifications only.
  const fetchNotifications = useCallback(() => fetchLevel(LEVEL_CRITICAL), [fetchLevel]);

  useEffect(() => {
    fetchNotifications();
    pollRef.current = setInterval(() => {
      if (!document.hidden) {
        fetchNotifications();
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  const togglePanel = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      fetchNotifications();
      fetchLevel(LEVEL_INFO);
    }
  };

  const markAllRead = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    try {
      await axios.post('/api/notifications/mark-read', { ids: null }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setInfoNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      setInfoUnreadCount(0);
    } catch (error) {
      // Next poll re-syncs the real state.
    }
  };

  const markOneRead = async (notification) => {
    if (notification.read) return;
    const token = localStorage.getItem('access_token');
    if (!token) return;
    try {
      await axios.post('/api/notifications/mark-read', { ids: [notification.id] }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const markRead = (prev) => prev.map(
        (n) => (n.id === notification.id ? { ...n, read: true } : n)
      );
      if (infoNotifications.some((n) => n.id === notification.id)) {
        setInfoNotifications(markRead);
        setInfoUnreadCount((prev) => Math.max(0, prev - 1));
      } else {
        setNotifications(markRead);
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      // Next poll re-syncs the real state.
    }
  };

  const formatRelativeTime = (isoDate) => {
    if (!isoDate) return '';
    const elapsedMs = Date.now() - new Date(isoDate).getTime();
    const minutes = Math.floor(elapsedMs / 60000);
    if (minutes < 1) return t('notificationBell.justNow', 'Just now');
    if (minutes < 60) return t('notificationBell.minutesAgo', '{{count}} min ago', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('notificationBell.hoursAgo', '{{count}}h ago', { count: hours });
    const days = Math.floor(hours / 24);
    return t('notificationBell.daysAgo', '{{count}}d ago', { count: days });
  };

  const iconForType = (type) => {
    if (type === 'external_sync_failed' || type === 'carrier_automation_failed') {
      return <FaExclamationTriangle className="una-notif-item-icon una-notif-icon-warning" />;
    }
    return <FaInfoCircle className="una-notif-item-icon una-notif-icon-info" />;
  };

  // Localized rendering for typed notifications; unknown types fall back to
  // the stored title/body strings.
  const textsForNotification = (notification) => {
    if (notification.type === 'carrier_automation_failed') {
      const data = notification.data || {};
      const diagnosis = getCarrierFailureTexts(t, data.category, data.carrier, data);
      const tracking = data.tracking_number || '—';
      return {
        title: t('carrierFailure.notifTitle', 'Carrier contact failed — {{carrier}}', {
          carrier: data.carrier || t('carrierFailure.genericCarrier', 'the carrier')
        }),
        // A category whose fix needs the whole sentence (client not attached
        // to a contract) shows it; the others show their title.
        body: diagnosis.notifBody
          ? t('carrierFailure.notifBodyFull', 'Tracking {{tracking}}: {{text}}', { tracking, text: diagnosis.notifBody })
          : t('carrierFailure.notifBody', 'Tracking {{tracking}}: {{title}}', { tracking, title: diagnosis.title })
      };
    }
    return { title: notification.title, body: notification.body };
  };

  const openNotification = (notification) => {
    markOneRead(notification);
    const ticketId = notification.data?.ticket_id;
    // In-app destination: `url` (older notifications) or `link` (shops, SMS,
    // voice numbers). Same-origin paths only.
    const url = notification.data?.url || notification.data?.link;
    if (ticketId) {
      setIsOpen(false);
      navigate(`/tickets/${ticketId}/messages`);
    } else if (typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')) {
      setIsOpen(false);
      navigate(url);
    }
  };

  const shown = level === LEVEL_INFO ? infoNotifications : notifications;
  const formatCount = (count) => (count > 99 ? '99+' : count);

  return (
    <div className="una-notif-bell" ref={containerRef}>
      <button
        type="button"
        className="una-notif-bell-button"
        onClick={togglePanel}
        title={t('notificationBell.title', 'Notifications')}
        aria-label={t('notificationBell.title', 'Notifications')}
      >
        <FaBell />
        {unreadCount > 0 && (
          <span className="una-notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="una-notif-panel">
          <div className="una-notif-panel-header">
            <span className="una-notif-panel-title">
              {t('notificationBell.title', 'Notifications')}
            </span>
            {unreadCount + infoUnreadCount > 0 && (
              <button type="button" className="una-notif-mark-all" onClick={markAllRead}>
                <FaCheckDouble />
                <span>{t('notificationBell.markAllRead', 'Mark all as read')}</span>
              </button>
            )}
          </div>
          <div className="una-notif-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={level === LEVEL_CRITICAL}
              className={`una-notif-tab${level === LEVEL_CRITICAL ? ' una-notif-tab-active' : ''}`}
              onClick={() => setLevel(LEVEL_CRITICAL)}
            >
              <span>{t('notificationBell.tabAlerts', 'Alerts')}</span>
              {unreadCount > 0 && (
                <span className="una-notif-tab-count una-notif-tab-count-alert">{formatCount(unreadCount)}</span>
              )}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={level === LEVEL_INFO}
              className={`una-notif-tab${level === LEVEL_INFO ? ' una-notif-tab-active' : ''}`}
              onClick={() => { setLevel(LEVEL_INFO); fetchLevel(LEVEL_INFO); }}
            >
              <span>{t('notificationBell.tabInfo', 'Information')}</span>
              {infoUnreadCount > 0 && (
                <span className="una-notif-tab-count">{formatCount(infoUnreadCount)}</span>
              )}
            </button>
          </div>
          <div className="una-notif-list" role="tabpanel">
            {shown.length === 0 ? (
              <div className="una-notif-empty">
                {level === LEVEL_INFO
                  ? t('notificationBell.emptyInfo', 'No information')
                  : t('notificationBell.empty', 'No notifications')}
              </div>
            ) : (
              shown.map((notification) => {
                const texts = textsForNotification(notification);
                return (
                  <button
                    type="button"
                    key={notification.id}
                    className={`una-notif-item${notification.read ? '' : ' una-notif-item-unread'}`}
                    onClick={() => openNotification(notification)}
                  >
                    {iconForType(notification.type)}
                    <div className="una-notif-item-content">
                      <span className="una-notif-item-title">{texts.title}</span>
                      <span className="una-notif-item-body">{texts.body}</span>
                      <span className="una-notif-item-time">
                        {formatRelativeTime(notification.created_at)}
                      </span>
                    </div>
                    {!notification.read && <span className="una-notif-unread-dot" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
